import { checkoutEnv } from './env';
import { InvalidPinError } from './errors';
import apiClient from '../../services/apiClient';

// -----------------------------------------------------------------------------
// Pair-user client — kiosk pairing backend (separate from portal-ui's /api).
// Uses raw fetch with `VITE_PAIR_API_BASE_URL` so it bypasses apiClient.
// -----------------------------------------------------------------------------
async function pairFetch(path, init) {
  const url = `${checkoutEnv.apiBaseUrl}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => undefined) : undefined;
  if (!res.ok) {
    const err = new Error(
      body && typeof body === 'object' && typeof body.detail === 'string'
        ? body.detail
        : `Request failed with status ${res.status}`
    );
    err.status = res.status;
    err.payload = body;
    throw err;
  }
  return body;
}

// -----------------------------------------------------------------------------
// Directory (orgs / sites / employees) — uses the real management-service API
// via the shared apiClient. Responses from the management service look like:
//   GET /api/organizations           -> { content: [{orgId, name, displayName, …}] }
//   GET /api/orgs/{orgId}/sites      -> { content: [{siteId, siteName, …}] }
//   GET /api/orgs/{orgId}/users      -> { content: [{userId, firstName, lastName,
//                                                   customerEmployeeId, …}] }
//     NOTE: the PK is serialised as `userId` (record field employeeId is
//     @JsonProperty("userId")). Paginates with limit/offset (default 50, max 200).
// -----------------------------------------------------------------------------
function unwrap(data) {
  if (Array.isArray(data)) return data;
  return data?.content || data?.items || [];
}

function buildEmployeeName(e) {
  const first = e.preferredFirstName || e.firstName || '';
  const last  = e.preferredLastName  || e.lastName  || '';
  const combined = `${first} ${last}`.trim();
  return combined || e.email || `Employee #${e.userId ?? e.employeeId}`;
}

// -----------------------------------------------------------------------------
// Offline mock directory data (only used if `VITE_USE_MOCKS=true`).
// -----------------------------------------------------------------------------
const MOCK_ORGS = [
  { id: '1', name: 'Walgreens (dev org #1)' },
  { id: '2', name: 'Target (dev org #2)' },
];
const MOCK_SITES = [
  { id: '1', orgId: '1', name: 'Walgreens #001 - Austin Downtown' },
  { id: '2', orgId: '1', name: 'Walgreens #042 - Round Rock' },
  { id: '1', orgId: '2', name: 'Target T-010 - Cedar Park' },
];
const MOCK_EMPLOYEES = [
  { id: 'john',   employeeId: '100001', name: 'John Doe',     orgId: '1', siteId: '1' },
  { id: 'u_1001', employeeId: '123567', name: 'Alice Wilson', orgId: '1', siteId: '1' },
  { id: 'u_2001', employeeId: '998001', name: 'Frank Ortiz',  orgId: '2', siteId: '1' },
];
const delay = (value, ms = 200) => new Promise((r) => setTimeout(() => r(value), ms));

export async function fetchOrganizations() {
  if (checkoutEnv.useDirectoryMocks) return delay(MOCK_ORGS);
  const res = await apiClient.get('/api/organizations', { params: { page: 0, size: 200 } });
  return unwrap(res.data).map((o) => ({
    id: String(o.orgId ?? o.id),
    name: o.displayName || o.name || `Org #${o.orgId ?? o.id}`,
  }));
}

export async function fetchSites(orgId) {
  if (checkoutEnv.useDirectoryMocks) return delay(MOCK_SITES.filter((s) => s.orgId === String(orgId)));
  const res = await apiClient.get(`/api/orgs/${encodeURIComponent(orgId)}/sites`, {
    params: { page: 0, size: 200 },
  });
  return unwrap(res.data).map((s) => ({
    id: String(s.siteId ?? s.id),
    orgId: String(orgId),
    name: s.siteName || s.name || `Site #${s.siteId ?? s.id}`,
  }));
}

export async function fetchEmployees(orgId, siteId) {
  if (checkoutEnv.useDirectoryMocks) {
    return delay(MOCK_EMPLOYEES.filter((e) => e.orgId === String(orgId) && e.siteId === String(siteId)));
  }
  // NOTE: the management-service list endpoint paginates with `limit`/`offset`
  // (default 50, max 200) — NOT `page`/`size`. Sending page/size silently caps
  // the list at the default 50 users.
  const res = await apiClient.get(`/api/orgs/${encodeURIComponent(orgId)}/users`, {
    params: { siteId, limit: 200, offset: 0 },
  });
  return unwrap(res.data).map((e) => ({
    // The management service serialises the employee PK as JSON field `userId`
    // (record field `employeeId` is @JsonProperty("userId")). Read `userId`
    // first; without it every option's value collapses to "undefined" and the
    // combobox always resolves to the first user.
    id: String(e.userId ?? e.employeeId ?? e.id),
    employeeId: String(e.customerEmployeeId ?? e.userId ?? e.employeeId ?? e.id),
    name: buildEmployeeName(e),
    orgId: String(orgId),
    siteId: String(siteId),
  }));
}

// -----------------------------------------------------------------------------
// Pair user — kiosk "I have my camera" flow. Asks the pairing service to
// pair a camera (identified by PIN) to the given employee.
//
// Payload uses the pairing service's field names:
//   { orgId, siteId, userId, pin }  — numeric UDM ids for org/site/user.
// -----------------------------------------------------------------------------
export async function pairUser({ orgId, siteId, userId, pin }) {
  if (checkoutEnv.useMockPairing) {
    await new Promise((r) => setTimeout(r, 500));
    if (pin === '1234') throw new InvalidPinError();
    return {
      status: 'paired',
      deviceId: 'DEV-MOCK-0001',
      userId: String(userId),
      pairedAt: new Date().toISOString(),
      batteryPct: 87,
      lowBatteryWarning: false,
      checkoutStatus: 'skipped',
    };
  }
  try {
    return await pairFetch('/api/v1/pair-user', {
      method: 'POST',
      body: JSON.stringify({
        orgId:  String(orgId),
        siteId: String(siteId),
        userId: String(userId),
        pin,
      }),
    });
  } catch (err) {
    if (err.status === 404) throw new InvalidPinError(err.message);
    throw err;
  }
}

/**
 * Auto-pair — kiosk "I need a camera" flow. Asks the pairing service to
 * assign an available camera from the dock to the given employee.
 *
 * Payload uses the pairing service's field names:
 *   { orgId, siteId, userId }  — numeric UDM ids. Names are rejected: UDM does
 *   not constrain tagout_name to be unique, so two namesakes are
 *   indistinguishable and would collide onto one pairing record.
 */
export async function autoPair({ orgId, siteId, userId }) {
  return pairFetch('/api/v1/auto-pair', {
    method: 'POST',
    body: JSON.stringify({
      orgId:  String(orgId),
      siteId: String(siteId),
      userId: String(userId),
    }),
  });
}
